import { ToastContentContainer } from 'client/components/misc/ToastContentContainer'
import { WarningIcon } from 'client/components/icons/Icon'
import toast from 'react-hot-toast'
import type { ToastOptions } from 'react-hot-toast'

import { generateRandomString } from 'lib/util'

type ToastStyle = ToastOptions['style']

interface ApiCallWithToastsOptions {
  apiCallFn: () => Promise<unknown>

  loadingToastMessage?: string
  loadingToastStyle?: ToastStyle
  loadingToastDuration?: number

  onSuccessFn?: (data: unknown) => void
  generateSuccessToastMessageFn?: (data: unknown) => string
  getWarningMessage?: (data: unknown) => string | null | undefined
  successToastActionFn?: (data: unknown) => void
  successToastActionLabel?: string
  successToastStyle?: ToastStyle
  successToastDuration?: number

  generateToastErrorActionLabelFn?: (err: unknown) => string | undefined
  handleToastErrorActionFn?: (err: unknown) => void
  onErrorFn?: () => void
  generateErrorToastMessageFn?: (err: unknown) => string
  errorToastStyle?: ToastStyle
  errorToastDuration?: number
}

export const apiCallWithToasts = async ({
  apiCallFn,

  loadingToastMessage,
  loadingToastStyle = {
    color: 'black',
    background: 'white',
    width: '500px',
    maxWidth: '90vw'
  },
  loadingToastDuration = 100000,

  onSuccessFn = () => {},
  generateSuccessToastMessageFn = () => '',
  getWarningMessage,
  successToastActionFn,
  successToastActionLabel = 'View',
  successToastStyle = {
    color: 'white',
    background: '#339941',
    width: '500px',
    maxWidth: '90vw'
  },
  successToastDuration = 5500,

  generateToastErrorActionLabelFn = () => undefined,
  handleToastErrorActionFn = () => {},
  onErrorFn = () => {},
  generateErrorToastMessageFn = () => '',
  errorToastStyle = {
    color: 'white',
    background: '#D04014',
    width: '500px',
    maxWidth: '90vw'
  },
  errorToastDuration = 7500
}: ApiCallWithToastsOptions): Promise<unknown> => {
  const toastID = generateRandomString()

  const result = await toast.promise(
    apiCallFn(),
    {
      loading: <ToastContentContainer toastID={`${toastID}:loading`} mainText={loadingToastMessage ?? ''} />,
      success: data => {
        if (onSuccessFn) {
          onSuccessFn(data)
        }
        toast.dismiss(`${toastID}:loading`)

        const warningMessage = getWarningMessage ? getWarningMessage(data) : null
        if (warningMessage) {
          setTimeout(() => toast.dismiss(`${toastID}:success`), 0)
          toast(
            t => (
              <ToastContentContainer
                toastID={t.id || ''}
                mainText={warningMessage}
                actionLabel={successToastActionFn ? successToastActionLabel : null}
                actionHandler={() => {
                  if (successToastActionFn) {
                    successToastActionFn(data)
                  }
                  toast.dismiss(t.id)
                }}
              />
            ),
            {
              duration: 7500,
              icon: <WarningIcon size={20} />,
              style: { width: '500px', maxWidth: '90vw', color: 'black', background: '#f1c40f' }
            }
          )
          return null
        }

        const successToastMessage = generateSuccessToastMessageFn ? generateSuccessToastMessageFn(data) : 'Success!'
        return (
          <ToastContentContainer
            toastID={`${toastID}:success`}
            mainText={successToastMessage}
            actionLabel={successToastActionFn ? successToastActionLabel : null}
            actionHandler={() => {
              if (successToastActionFn) {
                successToastActionFn(data)
              }
              toast.dismiss(`${toastID}:success`)
            }}
          />
        )
      },
      error: err => {
        if (onErrorFn) {
          onErrorFn()
        }
        const errorToastMessage = generateErrorToastMessageFn
          ? generateErrorToastMessageFn(err)
          : 'Uh-oh, something unexpected happened...'
        toast.dismiss(`${toastID}:loading`)
        const errorToastActionLabel = generateToastErrorActionLabelFn(err) || null

        return (
          <ToastContentContainer
            toastID={`${toastID}:error`}
            mainText={errorToastMessage}
            actionLabel={errorToastActionLabel}
            actionHandler={() => {
              if (handleToastErrorActionFn) {
                handleToastErrorActionFn(err)
              }
              toast.dismiss(`${toastID}:error`)
            }}
          />
        )
      }
    },
    {
      loading: {
        duration: loadingToastDuration,
        id: `${toastID}:loading`,
        style: loadingToastStyle
      },
      error: {
        duration: errorToastDuration,
        id: `${toastID}:error`,
        style: errorToastStyle
      },
      success: {
        duration: successToastDuration,
        id: `${toastID}:success`,
        style: successToastStyle
      }
    }
  )
  return result
}

interface InfoToastOptions {
  message: string
  durationMs?: number
}

export const infoToast = ({ message, durationMs }: InfoToastOptions): string => {
  const toastId = toast(t => <ToastContentContainer toastID={t.id || ''} mainText={message} />, {
    duration: durationMs,
    style: {
      width: '500px',
      maxWidth: '90vw',
      color: 'white',
      background: '#2563eb'
    }
  })
  return toastId
}

interface WarnToastOptions {
  message: string
  durationMs?: number
  showIcon?: boolean
}

export const warnToast = ({ message, durationMs, showIcon = false }: WarnToastOptions): string => {
  const toastId = toast(t => <ToastContentContainer toastID={t.id || ''} mainText={message} />, {
    duration: durationMs,
    icon: (showIcon && <WarningIcon size={20} />) || undefined,
    style: {
      width: '500px',
      maxWidth: '90vw',
      color: 'black',
      background: '#f1c40f'
    }
  })
  return toastId
}

interface SuccessToastOptions {
  message: string
  durationMs?: number
  actionLabel?: string
  actionHandler?: () => void
}

export const successToast = ({ message, durationMs, actionLabel, actionHandler }: SuccessToastOptions): string => {
  const toastId = toast.success(
    t => (
      <ToastContentContainer
        toastID={t.id || ''}
        mainText={message}
        actionLabel={actionHandler ? actionLabel : null}
        actionHandler={
          actionHandler
            ? () => {
                actionHandler()
                toast.dismiss(t.id)
              }
            : null
        }
      />
    ),
    {
      duration: durationMs,
      style: {
        width: '500px',
        maxWidth: '90vw',
        color: 'white',
        background: '#339941'
      }
    }
  )
  return toastId
}

interface ErrorToastOptions {
  message: string
  durationMs?: number
}

export const errorToast = ({ message, durationMs }: ErrorToastOptions): string => {
  const toastId = toast.error(t => <ToastContentContainer toastID={t.id || ''} mainText={message} />, {
    duration: durationMs,
    style: {
      width: '500px',
      maxWidth: '90vw',
      color: 'white',
      background: '#D04014'
    }
  })
  return toastId
}
